import { UncoveredList } from './Uncovered'
import { useState } from 'react'
import { translate, type Lang } from './i18n'
import { CrewBuilder } from './CrewBuilder'
import { ScenarioComparison } from './ScenarioComparison'
import { Tabs } from './Tabs'
import type { Evaluation, FormUnit, GenerateScenario, Scenario, ShiftRoster, ShiftSummary , Denial} from './api'
import { DataNote } from './DataNote'
import { useShiftCodes } from './shifts'


/**
 * Las perillas con las que se generó un escenario, en el idioma de quien mira.
 *
 * Antes iban dentro de la etiqueta, en una frase que el servidor armaba en español y guardaba en
 * la base. Ahora la etiqueta dice de dónde salió el escenario y las perillas se arman aquí, con la
 * política que el escenario ya traía.
 */
/** Arma el pedido con solo lo que el planificador cambió; lo vacío lo decide el perfil vigente. */
export function generateRequest(f: {
  preset: string; rule: string; revisit: string; hourly: string; lookahead: string; reserve: string
}): GenerateScenario {
  const r: GenerateScenario = {}
  if (f.preset) r.preset = f.preset
  if (f.rule) r.rule = f.rule
  if (f.revisit.trim() !== '' && Number.isFinite(Number(f.revisit))) r.revisitMinutes = Number(f.revisit)
  if (f.hourly) r.hourly = f.hourly === 'true'
  if (f.lookahead) r.lookahead = f.lookahead === 'true'
  if (f.reserve) r.reserve = f.reserve === 'true'
  return r
}

export function knobs(lang: Lang, policy: Record<string, string>): string {
  const si = translate(lang, 'planner.on')
  const no = translate(lang, 'planner.off')
  // La API camelliza la primera letra y los booleanos llegan como los escribe .NET («True»).
  const on = (k: string) => (policy[k]?.toLowerCase() === 'true' ? si : no)
  const base = translate(lang, 'planner.knobs', [
    on('useHourlyRisk'), on('useLookahead'), on('useResponseReserve'),
    policy['revisitMinutes'] ?? '—',
  ])
  // La regla de división y lo que mueve cada perfil de división: sin esto dos escenarios que
  // difieren justo en eso se veían iguales al compararlos (PL-04, H-95).
  const regla = policy['divisionRule']
    ? ` · ${translate(lang, `planner.rule.${policy['divisionRule'].toLowerCase()}`)}`
    : ''
  let divisiones = ''
  try {
    const por = JSON.parse(policy['byDivision'] ?? '{}') as Record<string, Record<string, number>>
    divisiones = Object.entries(por)
      .map(([d, k]) => translate(lang, 'planner.divisionKnobs', [d, k['RevisitMinutes'] ?? '—', k['DwellFactor'] ?? 1]))
      .map((t) => ` · ${t}`).join('')
  } catch { /* una política sin perfiles de división */ }
  return base + regla + divisiones
}

/**
 * La pantalla del Patrol Scheduler (§9.4).
 *
 * El orden de la columna es el del trabajo: con qué se cuenta, qué propone el motor, y qué se
 * vuelve el plan. Lo que se decide está abajo, después de haber mirado los números.
 */
export function PlannerScreen({
  lang, date, code, onShift, summary, scenarios, busy,
  onGenerate, onPropose, onApprove, onReject, canGenerate, canPropose, canApprove,
  roster, onEvaluate, onFormUnit, canForm, canExcept, loaded, problem,
}: {
  lang: Lang
  date: string
  code: string
  onShift: (date: string, code: string) => void
  summary: ShiftSummary | null
  scenarios: Scenario[]
  busy: boolean
  onGenerate: (r: GenerateScenario) => void
  onPropose: (scenarioId: string) => void
  onApprove: (planId: string, version: number, acknowledgePartial: boolean) => void
  /** Devolver la propuesta para correcciones, con el motivo (Figura 7). */
  onReject: (planId: string, version: number, reason: string) => void
  canGenerate: boolean
  canPropose: boolean
  canApprove: boolean
  roster: ShiftRoster | null
  onEvaluate: (r: FormUnit) => Promise<Evaluation | null>
  onFormUnit: (r: FormUnit) => void
  canForm: boolean
  canExcept: boolean
  loaded: boolean
  problem: Denial | null
}) {
  // El motivo para devolver una propuesta. Sin él no se puede: quien propuso tiene que saber
  // qué corregir, y un «no» sin razón solo lo hace volver a proponer lo mismo.
  const [motivo, setMotivo] = useState('')
  const [aceptoParcial, setAceptoParcial] = useState(false)
  // Vacío es «lo que dice el perfil vigente». Solo viaja lo que el planificador cambia a propósito
  // (hallazgo H-10: antes todo viajaba siempre, y lo aprobado por el mando no se aplicaba).
  const [preset, setPreset] = useState('')
  const [rule, setRule] = useState('')
  const [revisit, setRevisit] = useState('')
  const [hourly, setHourly] = useState('')
  const [lookahead, setLookahead] = useState('')
  const [reserve, setReserve] = useState('')
  // Los dos que se están comparando. Al marcar un tercero se suelta el más viejo: comparar de a
  // dos es lo que cabe en una tabla legible, y lo que el planner de verdad hace.
  const [compared, setCompared] = useState<string[]>([])
  const shifts = useShiftCodes()
  const pareja = compared
    .map((id) => scenarios.find((s) => s.scenarioId === id))
    .filter((s): s is Scenario => s !== undefined)

  const pct = (x: number) => x.toFixed(1)

  // Sin el resumen todavía no se sabe: no se bloquea por las dudas.
  const sinUnidades = summary !== null && summary.units.length === 0

  return (
    <div className="layout single">
      <aside className="side full">
        <p className="notice">{translate(lang, 'planner.help')}</p>

        {/*
          Por los pasos de §9.4: mirar qué hay, conformar unidades, generar y comparar escenarios,
          y publicar el plan. Seis paneles apilados obligaban a bajar media pantalla para llegar al
          último, y las tablas no cabían en el ancho de antes.
        */}
        <Tabs
          area="planner"
          tabs={[
            {
              id: 'turno',
              label: translate(lang, 'planner.tabShift'),
              content: (
                <>
            <section className="panel">
              <h2>{translate(lang, 'planner.shift')}</h2>
              <div className="card">
                <div className="actions">
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => onShift(e.target.value, code)}
                    aria-label={translate(lang, 'planner.shift')}
                  />
                  <select value={code} onChange={(e) => onShift(date, e.target.value)}
                    aria-label={translate(lang, 'planner.shift')}>
                    {shifts.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                {summary && (
                  <div className="meta">
                    {translate(lang, 'planner.officers', [summary.available.officers])} ·{' '}
                    {translate(lang, 'planner.vehicles', [summary.available.vehicles])}
                  </div>
                )}
              </div>
            </section>

            {(summary?.coverage.length ?? 0) > 0 && (
              <section className="panel">
                <h2>{translate(lang, 'coverage.title')}</h2>
                <p className="notice">{translate(lang, 'coverage.help')}</p>
                <div className="card">
                  <table className="compare">
                    <thead>
                      <tr>
                        <th>{translate(lang, 'coverage.division')}</th>
                        <th>{translate(lang, 'coverage.officers')}</th>
                        <th>{translate(lang, 'coverage.sergeants')}</th>
                        <th>{translate(lang, 'coverage.vehicles')}</th>
                        <th>{translate(lang, 'coverage.formed')}</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {(summary?.coverage ?? []).map((c) => (
                        <tr key={c.division}>
                          <td>{c.division}</td>
                          <td className="num">{c.officers} / {c.positions}</td>
                          <td className="num">{c.sergeants} / {c.requiredSergeants}</td>
                          <td className="num">{c.vehicles} / {c.requiredUnits}</td>
                          <td className="num">{c.formed}</td>
                          <td className={c.shortfalls.length === 0 ? 'win' : ''}>
                            {c.shortfalls.length === 0
                              ? translate(lang, 'coverage.ok')
                              : c.shortfalls.map((s) => {
                                const [que, cuantos] = s.split(':')
                                return translate(lang,
                                  que === 'officers' ? 'coverage.shortOfficers'
                                    : que === 'sergeants' ? 'coverage.shortSergeants'
                                      : 'coverage.shortVehicles', [cuantos ?? ''])
                              }).join(' · ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

                </>
              ),
            },
            {
              id: 'unidades',
              label: translate(lang, 'planner.tabUnits'),
              content: (
                <>
            {canForm && (
              <CrewBuilder
                lang={lang}
                roster={roster}
                evaluate={onEvaluate}
                onForm={onFormUnit}
                busy={busy}
                canExcept={canExcept}
              />
            )}

            <section className="panel">
              <h2>{translate(lang, 'planner.units', [summary?.units.length ?? 0])}</h2>
              {(summary?.units.length ?? 0) === 0 && <p className="notice">{translate(lang, 'planner.noUnits')}</p>}
              {(summary?.units ?? []).map((u) => (
                <div key={u.patrolUnitId} className="card">
                  <div className="row-line">
                    <h3 className="grow">{u.callSign} · {u.division}</h3>
                    <span className="chip">{translate(lang, `unit.${u.status}`)}</span>
                  </div>
                  {/* Con los nombres que usa la gente: el tipo, el número pintado del vehículo y las
                      placas de la tripulación. Antes salía «PATROL_1 · FWSYN-V-A101 · 1». */}
                  <div className="meta">
                    {roster?.unitTypes.find((t) => t.code === u.unitTypeCode)?.name ?? u.unitTypeCode}
                    {' · '}{roster?.vehicles.find((v) => v.vehicleId === u.vehicleId)?.unitNumber ?? u.vehicleId}
                    {' · '}{(() => {
                      const placas = (roster?.officers ?? []).filter((o) => o.assignedTo === u.callSign).map((o) => o.badge)
                      return placas.length > 0 ? placas.join(', ') : translate(lang, 'planner.crewSize', [u.crewSize])
                    })()}
                  </div>
                </div>
              ))}
            </section>
                </>
              ),
            },
            {
              id: 'escenarios',
              label: translate(lang, 'planner.tabScenarios'),
              content: (
                <>
            {canGenerate && (
              <section className="panel">
                <h2>{translate(lang, 'planner.generate')}</h2>
                <p className="notice">{translate(lang, 'planner.tip')}</p>
                <div className="card">
                  <div className="actions">
                    <select value={preset} onChange={(e) => setPreset(e.target.value)}
                      aria-label={translate(lang, 'planner.preset')}>
                      <option value="">{translate(lang, 'planner.fromProfile')}</option>
                      <option value="protect-base">{translate(lang, 'planner.preset.protect-base')}</option>
                      <option value="base">{translate(lang, 'planner.preset.base')}</option>
                    </select>
                    <select value={rule} onChange={(e) => setRule(e.target.value)}
                      aria-label={translate(lang, 'planner.rule')}>
                      <option value="">{translate(lang, 'planner.fromProfile')}</option>
                      <option value="prefer">{translate(lang, 'planner.rule.prefer')}</option>
                      <option value="strict">{translate(lang, 'planner.rule.strict')}</option>
                      <option value="ignore">{translate(lang, 'planner.rule.ignore')}</option>
                    </select>
                    <input
                      type="number" min={15} max={480} value={revisit} style={{ width: 110 }}
                      placeholder={translate(lang, 'planner.fromProfile')}
                      onChange={(e) => setRevisit(e.target.value)}
                      aria-label={translate(lang, 'planner.revisit')}
                    />
                  </div>
                  <div className="actions">
                    {([
                      ['planner.hourly', hourly, setHourly],
                      ['planner.lookahead', lookahead, setLookahead],
                      ['planner.reserve', reserve, setReserve],
                    ] as const).map(([clave, valor, poner]) => (
                      <label key={clave}>
                        {translate(lang, clave)}{' '}
                        <select value={valor} onChange={(e) => poner(e.target.value)} aria-label={translate(lang, clave)}>
                          <option value="">{translate(lang, 'planner.fromProfile')}</option>
                          <option value="true">{translate(lang, 'planner.on')}</option>
                          <option value="false">{translate(lang, 'planner.off')}</option>
                        </select>
                      </label>
                    ))}
                  </div>
                  <p className="notice">{translate(lang, 'planner.fromProfileHelp')}</p>
                  {/* Sin unidades no hay escenario: se dice qué falta y a quién pedirlo, en vez de
                      generar uno vacío que se podía proponer (PL-00, H-93). */}
                  {sinUnidades && (
                    <p className="notice warning">
                      {translate(lang, (summary?.available.officers ?? 0) === 0
                        ? 'planner.needsAvailability' : 'planner.needsUnits')}
                    </p>
                  )}
                  <button
                    className="primary"
                    disabled={busy || sinUnidades}
                    onClick={() => onGenerate(generateRequest({ preset, rule, revisit, hourly, lookahead, reserve }))}
                  >
                    {translate(lang, 'planner.run')}
                  </button>
                </div>
              </section>
            )}

            <section className="panel">
              <h2>{translate(lang, 'planner.scenarios', [scenarios.length])}</h2>
              <DataNote lang={lang} loaded={loaded} problem={problem} count={scenarios.length} empty="planner.noScenarios" />
              {scenarios.length > 1 && pareja.length < 2 &&
                <p className="notice">{translate(lang, 'compare.hint')}</p>}
              {pareja.length === 2 && <ScenarioComparison lang={lang} a={pareja[0]!} b={pareja[1]!} />}
              {scenarios.map((s) => (
                <div key={s.scenarioId} className={`card ${s.isPartial ? 'warning' : ''}`}>
                  <div className="row-line">
                    <h3 className="grow">{s.label || s.scenarioId.slice(0, 8)}</h3>
                    <label className="meta">
                      <input
                        type="checkbox"
                        checked={compared.includes(s.scenarioId)}
                        onChange={(e) => setCompared(e.target.checked
                          ? [...compared, s.scenarioId].slice(-2)
                          : compared.filter((x) => x !== s.scenarioId))}
                      />
                      {' '}{translate(lang, 'compare.pick')}
                    </label>
                    <span className="chip">{s.engineVersion}</span>
                  </div>
                  <div className="meta">{knobs(lang, s.policy)}</div>
                  <div className="meta">
                    {translate(lang, 'planner.kpis', [
                      pct(s.kpis.coverage), pct(s.kpis.riskWeightedCoverage),
                      s.kpis.visits, s.kpis.expectedVisits, pct(100 * s.kpis.utilization),
                    ])}
                  </div>
                  {s.kpis.hotspotsNotServed > 0 &&
                    <div className="meta">{translate(lang, 'planner.partial', [s.kpis.hotspotsNotServed])}</div>}
                  {s.kpis.crossDivisionVisits > 0 &&
                    <div className="meta">{translate(lang, 'planner.crossDivision', [s.kpis.crossDivisionVisits])}</div>}
                  <div className="meta">{translate(lang, 'analyst.createdBy', [s.createdBy])}</div>
                  {/* El que ya se propuso lo dice, con a quién le toca: proponer no daba ninguna
                      señal y había que ir a buscarlo a la pestaña Plan (H-96). */}
                  {summary?.proposed?.scenarioId === s.scenarioId ? (
                    <span className="chip ok" role="status">
                      {translate(lang, 'planner.proposedAs', [summary.proposed.version])}
                    </span>
                  ) : canPropose && (
                    <div className="actions">
                      <button disabled={busy} onClick={() => onPropose(s.scenarioId)}>
                        {translate(lang, 'planner.propose')}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </section>

                </>
              ),
            },
            {
              id: 'plan',
              label: translate(lang, 'planner.tabPlan'),
              content: (
                <>
            <section className="panel">
              <h2>{translate(lang, 'planner.plan')}</h2>
              {summary?.plan ? (
                <div className="card">
                  <div className="meta">
                    {translate(lang, 'planner.planLine',
                      [summary.plan.version, summary.plan.assignments, summary.plan.proposedBy])}
                  </div>
                  {summary.plan.approvedBy &&
                    <div className="meta">{translate(lang, 'planner.approvedBy', [summary.plan.approvedBy])}</div>}
                  <div className="meta">
                    {Object.entries(summary.plan.byStatus)
                      .map(([estado, n]) => `${translate(lang, `assignment.${estado}`)}: ${n}`)
                      .join(' · ')}
                  </div>
                </div>
              ) : (
                <p className="notice">{translate(lang, 'planner.noPlan')}</p>
              )}

              {/* Lo que hay que corregir, hasta que se proponga otra cosa (hallazgo H-11). */}
              {summary?.returned && !summary.proposed && (
                <div className="card warning">
                  <strong>{translate(lang, 'planner.returned', [summary.returned.version, summary.returned.decidedBy])}</strong>
                  <div>{summary.returned.reason}</div>
                </div>
              )}

              {summary?.proposed && (
                <div className="card warning">
                  <div className="meta">
                    {translate(lang, 'planner.pending', [summary.proposed.version, summary.proposed.proposedBy])}
                  </div>
                  {/* Lo que se firma, a la vista de quien firma (hallazgo H-12). */}
                  {(() => {
                    const s = scenarios.find((x) => x.scenarioId === summary.proposed!.scenarioId)
                    if (!s) return null
                    return (
                      <>
                        <div className="meta">{s.label} · {knobs(lang, s.policy)}</div>
                        <div className="meta">
                          {translate(lang, 'planner.kpis', [
                            pct(s.kpis.coverage), pct(s.kpis.riskWeightedCoverage),
                            s.kpis.visits, s.kpis.expectedVisits, pct(100 * s.kpis.utilization),
                          ])}
                        </div>
                        {s.kpis.hotspotsNotServed > 0 &&
                          <div className="meta">{translate(lang, 'planner.partial', [s.kpis.hotspotsNotServed])}</div>}
                        {s.kpis.crossDivisionVisits > 0 &&
                          <div className="meta">{translate(lang, 'planner.crossDivision', [s.kpis.crossDivisionVisits])}</div>}
                      </>
                    )
                  })()}
                  {canApprove && (() => {
                    // Un plan parcial deja hotspots sin ninguna visita. Aprobarlo es aceptarlo, y eso
                    // lo tiene que decir quien firma, no la pantalla por él (hallazgo H-14).
                    const s = scenarios.find((x) => x.scenarioId === summary.proposed!.scenarioId)
                    const parcial = s?.isPartial ?? false
                    return (
                      <div className="actions">
                        {parcial && <UncoveredList lang={lang} items={s?.uncovered} />}
                        {parcial && (
                          <label>
                            <input type="checkbox" checked={aceptoParcial} disabled={busy}
                              onChange={(e) => setAceptoParcial(e.target.checked)} />
                            {' '}{translate(lang, 'planner.acceptPartial', [s?.kpis.hotspotsNotServed ?? 0])}
                          </label>
                        )}
                        <button
                          className="primary"
                          disabled={busy || (parcial && !aceptoParcial)}
                          onClick={() => {
                            onApprove(summary.proposed!.planId, summary.proposed!.version, parcial && aceptoParcial)
                            setAceptoParcial(false)
                          }}
                        >
                          {translate(lang, 'planner.approve')}
                        </button>
                      </div>
                    )
                  })()}
                  {canApprove && (
                    <div className="row-line" style={{ flexWrap: 'wrap' }}>
                      <input
                        className="grow"
                        value={motivo}
                        disabled={busy}
                        aria-label={translate(lang, 'planner.rejectReason')}
                        placeholder={translate(lang, 'planner.rejectReason')}
                        onChange={(e) => setMotivo(e.target.value)}
                      />
                      <button
                        disabled={busy || motivo.trim() === ''}
                        onClick={() => {
                          onReject(summary.proposed!.planId, summary.proposed!.version, motivo.trim())
                          setMotivo('')
                        }}
                      >
                        {translate(lang, 'planner.reject')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </section>

                </>
              ),
            },
          ]}
        />
      </aside>
    </div>
  )
}
